import {
  IconAlertTriangleFilled,
  IconBellFilled,
  IconCircleCheckFilled,
  IconClipboardListFilled,
  IconInfoCircleFilled,
  IconMail,
  IconMailOpened,
} from "@tabler/icons-react";
import { Badge, Empty, message, Popover, Spin, Tabs, Tooltip } from "antd";
import { useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { orpcClient } from "@/lib/orpc-client";

type NotificationCategory =
  | "approval"
  | "inventory"
  | "finance"
  | "hr"
  | "system";

type AppNotification = Awaited<
  ReturnType<typeof orpcClient.notification.list>
>[number];

type NotificationType = AppNotification["type"];

const NOTIFICATION_LIMIT = 50;

const TYPE_CATEGORY: Record<NotificationType, NotificationCategory> = {
  "purchaseOrder.pendingApproval": "approval",
  "productInventory.belowSafetyStock": "inventory",
  "accountsPayableDetail.paymentDueReminder": "finance",
  "accountsReceivableDetail.paymentDueReminder": "finance",
  "accountsReceivable.billingDueReminder": "finance",
  "accountsReceivable.performanceBondRetrievalReminder": "finance",
  "accountsReceivable.warrantyBondRetrievalReminder": "finance",
  "accountsReceivable.engineeringInsuranceExpiryReminder": "finance",
};

const categoryStyles: Record<
  NotificationCategory,
  { icon: JSX.Element; bg: string }
> = {
  approval: {
    icon: <IconClipboardListFilled size={18} className="text-blue-500" />,
    bg: "bg-blue-50",
  },
  inventory: {
    icon: <IconAlertTriangleFilled size={18} className="text-amber-500" />,
    bg: "bg-amber-50",
  },
  finance: {
    icon: <IconInfoCircleFilled size={18} className="text-violet-500" />,
    bg: "bg-violet-50",
  },
  hr: {
    icon: <IconCircleCheckFilled size={18} className="text-emerald-500" />,
    bg: "bg-emerald-50",
  },
  system: {
    icon: <IconBellFilled size={18} className="text-gray-500" />,
    bg: "bg-gray-100",
  },
};

const NotificationCenter = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "unread">("all");
  const [navigatingId, setNavigatingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const loadUnreadCount = useCallback(async () => {
    try {
      const count = await orpcClient.notification.unreadCount({});
      setUnreadCount(count);
    } catch {
      // 靜默失敗：徽章數量非關鍵，開啟面板時會再嘗試
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orpcClient.notification.list({
        limit: NOTIFICATION_LIMIT,
      });
      setNotifications(data);
    } catch {
      message.error(t("notificationCenter.loadError") || "無法載入通知");
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  useEffect(() => {
    if (open) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [open, loadNotifications, loadUnreadCount]);

  const resolveContent = (notification: AppNotification) => {
    const category = TYPE_CATEGORY[notification.type] ?? "system";
    const title = t(`notificationCenter.types.${notification.type}.title`, {
      defaultValue: t("notificationCenter.fallbackTitle") || "系統通知",
    });
    const description = t(
      `notificationCenter.types.${notification.type}.description`,
      { defaultValue: "" },
    );
    return { category, title, description };
  };

  const formatRelativeTime = (createdAt: string | Date) => {
    const created = dayjs(createdAt);
    const diffMinutes = dayjs().diff(created, "minute");
    if (diffMinutes < 1) return t("notificationCenter.justNow");
    if (diffMinutes < 60)
      return t("notificationCenter.minutesAgo", { count: diffMinutes });
    const diffHours = dayjs().diff(created, "hour");
    if (diffHours < 24)
      return t("notificationCenter.hoursAgo", { count: diffHours });
    const diffDays = dayjs().diff(created, "day");
    if (diffDays < 7)
      return t("notificationCenter.daysAgo", { count: diffDays });
    return created.format("YYYY/MM/DD");
  };

  const setReadState = async (
    notification: AppNotification,
    isRead: boolean,
  ) => {
    if (notification.isRead === isRead) return;
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, isRead } : n)),
    );
    setUnreadCount((c) => (isRead ? Math.max(0, c - 1) : c + 1));
    try {
      await orpcClient.notification.markRead({
        id: notification.id,
        isRead,
      });
    } catch {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id ? { ...n, isRead: !isRead } : n,
        ),
      );
      setUnreadCount((c) => (isRead ? c + 1 : Math.max(0, c - 1)));
      message.error(t("notificationCenter.updateError") || "更新通知狀態失敗");
    }
  };

  const handleToggleRead = async (
    event: React.MouseEvent,
    notification: AppNotification,
  ) => {
    event.stopPropagation();
    if (togglingId) return;
    setTogglingId(notification.id);
    try {
      await setReadState(notification, !notification.isRead);
    } finally {
      setTogglingId(null);
    }
  };

  const resolveRoute = async (
    notification: AppNotification,
  ): Promise<string | null> => {
    const sourceId = (notification.params as { sourceId?: string } | null)
      ?.sourceId;
    if (!sourceId) return null;

    switch (notification.type) {
      case "accountsReceivable.performanceBondRetrievalReminder":
      case "accountsReceivable.warrantyBondRetrievalReminder":
      case "accountsReceivable.engineeringInsuranceExpiryReminder":
      case "accountsReceivable.billingDueReminder":
        return `/hrMainPage/editAccountReceivable/${sourceId}`;
      case "purchaseOrder.pendingApproval":
        return `/hrMainPage/purchaseOrder/${sourceId}`;
      case "productInventory.belowSafetyStock":
        return `/hrMainPage/product/${sourceId}`;
      case "accountsReceivableDetail.paymentDueReminder": {
        const list = await orpcClient.accountsReceivable.list({});
        const parent = list.find((r) =>
          r.accountsReceivableDetail?.some((d) => d.id === sourceId),
        );
        return parent ? `/hrMainPage/editAccountReceivable/${parent.id}` : null;
      }
      case "accountsPayableDetail.paymentDueReminder": {
        const list = await orpcClient.accountsPayable.list({});
        const parent = list.find((r) =>
          r.accountsPayableDetail?.some((d) => d.id === sourceId),
        );
        return parent ? `/hrMainPage/editAccountPayable/${parent.id}` : null;
      }
      default:
        return null;
    }
  };

  const handleNotificationClick = async (notification: AppNotification) => {
    if (navigatingId) return;
    setReadState(notification, true);
    setNavigatingId(notification.id);
    try {
      const route = await resolveRoute(notification);
      if (route) {
        setOpen(false);
        navigate(route);
      }
    } catch {
      message.error(t("notificationCenter.navigateError") || "無法跳轉至來源頁面");
    } finally {
      setNavigatingId(null);
    }
  };

  const markAllAsRead = async () => {
    if (unreadCount === 0) return;
    const prevNotifications = notifications;
    const prevUnread = unreadCount;
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await orpcClient.notification.markAllRead({});
    } catch {
      setNotifications(prevNotifications);
      setUnreadCount(prevUnread);
      message.error(t("notificationCenter.updateError") || "更新通知狀態失敗");
    }
  };

  const visibleNotifications =
    activeTab === "unread"
      ? notifications.filter((n) => !n.isRead)
      : notifications;

  const renderList = () => {
    if (loading && notifications.length === 0) {
      return (
        <div className="flex justify-center py-10">
          <Spin />
        </div>
      );
    }

    if (visibleNotifications.length === 0) {
      return (
        <div className="py-10">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("notificationCenter.empty")}
          />
        </div>
      );
    }

    return (
      <div className="max-h-96 overflow-y-auto">
        {visibleNotifications.map((notification) => {
          const { category, title, description } = resolveContent(notification);
          const style = categoryStyles[category];
          return (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`flex cursor-pointer gap-3 border-b border-gray-100 px-4 py-3 transition-colors last:border-b-0 hover:bg-gray-50 ${
                notification.isRead ? "" : "bg-blue-50/40"
              } ${navigatingId === notification.id ? "pointer-events-none opacity-60" : ""}`}
            >
              <div
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${style.bg}`}
              >
                {style.icon}
              </div>
              <div className="min-w-0 flex-1">
                <div
                  className={`truncate text-sm ${
                    notification.isRead
                      ? "font-normal text-gray-700"
                      : "font-semibold text-gray-900"
                  }`}
                >
                  {title}
                </div>
                {description && (
                  <div className="mt-0.5 line-clamp-2 text-xs text-gray-500">
                    {description}
                  </div>
                )}
                <div className="mt-1 text-xs text-gray-400">
                  {formatRelativeTime(notification.createdAt)}
                </div>
              </div>
              <Tooltip
                title={
                  notification.isRead
                    ? t("notificationCenter.markUnread") || "標為未讀"
                    : t("notificationCenter.markRead") || "標為已讀"
                }
              >
                <div
                  onClick={(event) => handleToggleRead(event, notification)}
                  className={`flex h-6 w-6 shrink-0 self-center items-center justify-center rounded-full transition-colors hover:bg-gray-200 ${
                    togglingId === notification.id
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }`}
                >
                  {notification.isRead ? (
                    <IconMailOpened size={16} className="text-gray-400" />
                  ) : (
                    <IconMail size={16} className="text-blue-500" />
                  )}
                </div>
              </Tooltip>
            </div>
          );
        })}
      </div>
    );
  };

  const content = (
    <div className="w-80">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <span className="text-base font-semibold text-gray-900">
          {t("notificationCenter.title")}
        </span>
        <span
          onClick={unreadCount > 0 ? markAllAsRead : undefined}
          className={`text-sm ${
            unreadCount > 0
              ? "cursor-pointer text-blue-500 hover:text-blue-700"
              : "cursor-default text-gray-300"
          }`}
        >
          {t("notificationCenter.markAllRead")}
        </span>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as "all" | "unread")}
        centered
        size="small"
        className="px-4"
        items={[
          { key: "all", label: t("notificationCenter.tabAll") },
          {
            key: "unread",
            label: `${t("notificationCenter.tabUnread")}${
              unreadCount > 0 ? ` (${unreadCount})` : ""
            }`,
          },
        ]}
      />
      {renderList()}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomRight"
      content={content}
      overlayInnerStyle={{ padding: 0 }}
    >
      <div className="ml-4 flex flex-row items-center cursor-pointer">
        <div className="mr-1 text-lg text-gray-800">
          {t("notificationCenter.title")}
        </div>
        <Badge count={unreadCount} size="small" offset={[2, -2]}>
          <IconBellFilled className="text-gray-800" />
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationCenter;
